import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  AtSign,
  Bold,
  ChevronDown,
  Code,
  Indent,
  Italic,
  Link2,
  List,
  ListOrdered,
  MoreHorizontal,
  Outdent,
  Quote,
  RemoveFormatting,
  SquareCode,
  Strikethrough,
} from "lucide-react";
import Avatar from "../ui/Avatar";
import { toEditorHtml } from "./richText";

// ─── @mention chip (stored as <span class="mention" data-id data-name>) ─────
const Embed = Quill.import("blots/embed");
class MentionBlot extends Embed {
  static blotName = "mention";
  static tagName = "span";
  static className = "mention";

  static create(data) {
    const node = super.create(data);
    node.setAttribute("data-id", data.id);
    node.setAttribute("data-name", data.name);
    node.textContent = `@${data.name}`;
    return node;
  }

  static value(node) {
    return { id: node.getAttribute("data-id"), name: node.getAttribute("data-name") };
  }
}
if (!Quill.imports["formats/mention"]) Quill.register("formats/mention", MentionBlot);

// Lists nest three levels deep (indent 0, 1, 2).
const MAX_LIST_INDENT = 2;

const FORMATS = ["header", "bold", "italic", "strike", "code", "list", "bullet", "indent", "link", "code-block", "blockquote", "mention"];
const MENTION_QUERY = /(^|\s)@([^\s@]{0,30})$/;

const TEXT_STYLES = [
  { value: false, label: "Normal text", cls: "text-[14px]" },
  { value: 1, label: "Heading 1", cls: "text-[20px] font-semibold" },
  { value: 2, label: "Heading 2", cls: "text-[17px] font-semibold" },
  { value: 3, label: "Heading 3", cls: "text-[15px] font-semibold" },
];

const ToolButton = ({ active, onClick, label, children, disabled }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    // Keep the editor selection while clicking toolbar buttons.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`h-8 min-w-[32px] px-1.5 rounded-lg flex items-center justify-center transition-colors disabled:opacity-40 ${
      active ? "bg-accent-soft text-accent-ink" : "text-muted hover:text-ink hover:bg-hover"
    }`}
  >
    {children}
  </button>
);

const Menu = ({ open, onClose, children, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      onMouseDown={(e) => e.target.tagName !== "INPUT" && e.preventDefault()}
      className={`absolute z-40 top-full mt-1 rounded-xl border border-line2 bg-panel-solid shadow-modal p-1 ${className}`}
    >
      {children}
    </div>
  );
};

const MenuItem = ({ onClick, children, active }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-[13px] whitespace-nowrap ${
      active ? "bg-accent-soft text-accent-ink" : "text-ink hover:bg-hover"
    }`}
  >
    {children}
  </button>
);

/**
 * Trello-style rich text editor (Quill underneath).
 *  - Toolbar: text style, bold, italic, more (strike / inline code / clear),
 *    bullet + numbered lists, link, code block, quote, and @mention.
 *  - "@" (or the @ button) opens people suggestions; picks become chips.
 *  - Ctrl/⌘ + Enter calls onSubmit.
 *  - `compact`: the toolbar only shows while the editor is focused.
 * Value is HTML; plain-text input is converted on load.
 * Ref API: focus(), clear(), isEmpty(), getMentionIds().
 */
const TrelloEditor = forwardRef(
  (
    {
      value,
      onChange,
      placeholder = "",
      mentionable = [],
      onSubmit,
      autoFocus = false,
      disabled = false,
      compact = false,
      minHeight = 120,
      ariaLabel = "Editor",
    },
    ref,
  ) => {
    const quillRef = useRef(null);
    const wrapRef = useRef(null);
    const [initial] = useState(() => toEditorHtml(value || ""));
    const [formats, setFormats] = useState({});
    const [focused, setFocused] = useState(false);
    const [menu, setMenu] = useState(null); // "style" | "more" | "link"
    const [linkUrl, setLinkUrl] = useState("");
    const [mention, setMention] = useState(null); // { query, index, top, left }
    const [highlight, setHighlight] = useState(0);
    const editor = () => quillRef.current?.getEditor();

    const suggestions = useMemo(() => {
      if (!mention) return [];
      const q = mention.query.toLowerCase();
      return mentionable
        .filter((p) => (p.fullNameEn || "").toLowerCase().includes(q) || (p.nickname || "").toLowerCase().includes(q))
        .slice(0, 6);
    }, [mention, mentionable]);

    const refreshFormats = useCallback(() => {
      const q = editor();
      const range = q?.getSelection();
      setFormats(range ? q.getFormat(range) : {});
    }, []);

    const checkMention = useCallback(() => {
      const q = editor();
      const range = q?.getSelection();
      if (!q || !range || !mentionable.length) return setMention(null);
      const before = q.getText(Math.max(0, range.index - 32), Math.min(32, range.index));
      const m = before.match(MENTION_QUERY);
      if (!m) return setMention(null);
      const start = range.index - m[2].length - 1;
      const b = q.getBounds(range.index);
      setMention({ query: m[2], index: start, top: b.top + b.height + 6, left: Math.max(0, b.left - 8) });
      setHighlight(0);
    }, [mentionable.length]);

    const pickMention = useCallback(
      (person) => {
        const q = editor();
        if (!q || !mention) return;
        const sel = q.getSelection(true);
        q.deleteText(mention.index, sel.index - mention.index, "user");
        q.insertEmbed(mention.index, "mention", { id: person._id, name: person.fullNameEn || person.nickname }, "user");
        q.insertText(mention.index + 1, " ", "user");
        q.setSelection(mention.index + 2, 0, "user");
        setMention(null);
      },
      [mention],
    );

    useImperativeHandle(ref, () => ({
      focus: () => editor()?.focus(),
      clear: () => editor()?.setContents([], "silent"),
      isEmpty: () => {
        const q = editor();
        if (!q) return true;
        return !q.getText().trim() && !q.getContents().ops.some((op) => op.insert && typeof op.insert === "object");
      },
      getMentionIds: () => {
        const q = editor();
        if (!q) return [];
        return [
          ...new Set(
            q
              .getContents()
              .ops.filter((op) => op.insert?.mention?.id)
              .map((op) => op.insert.mention.id),
          ),
        ];
      },
    }));

    // Keyboard: mention navigation and Ctrl/⌘+Enter. Capture phase so it runs
    // before Quill's own Enter handling.
    useEffect(() => {
      const wrap = wrapRef.current;
      if (!wrap) return undefined;
      const onKey = (e) => {
        if (mention && suggestions.length && ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          if (e.key === "ArrowDown") setHighlight((h) => (h + 1) % suggestions.length);
          else if (e.key === "ArrowUp") setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
          else if (e.key === "Escape") setMention(null);
          else pickMention(suggestions[highlight]);
          return;
        }
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && onSubmit) {
          e.preventDefault();
          e.stopPropagation();
          onSubmit();
        }
      };
      wrap.addEventListener("keydown", onKey, true);
      return () => wrap.removeEventListener("keydown", onKey, true);
    }, [mention, suggestions, highlight, pickMention, onSubmit]);

    useEffect(() => {
      if (autoFocus) setTimeout(() => editor()?.focus(), 0);
    }, [autoFocus]);

    // Tab / Shift+Tab nest a list item under the previous one (max three levels),
    // from anywhere in the item (Quill's own binding only works at its start).
    useEffect(() => {
      const q = editor();
      if (!q) return;
      const TAB = 9;
      q.keyboard.addBinding({
        key: TAB,
        shiftKey: false,
        format: ["list"],
        handler(range, context) {
          if ((context.format.indent || 0) < MAX_LIST_INDENT) q.format("indent", "+1", "user");
          return false;
        },
      });
      q.keyboard.addBinding({
        key: TAB,
        shiftKey: true,
        format: ["list"],
        handler(range, context) {
          if ((context.format.indent || 0) > 0) q.format("indent", "-1", "user");
          return false;
        },
      });
      // Run ours before Quill's defaults.
      const list = q.keyboard.bindings[TAB];
      list.unshift(list.pop());
      list.unshift(list.pop());
    }, []);

    const nest = (delta) => {
      const q = editor();
      if (!q) return;
      q.focus();
      const level = q.getFormat().indent || 0;
      const next = level + delta;
      if (next < 0 || next > MAX_LIST_INDENT) return;
      q.format("indent", next === 0 ? false : next, "user");
      refreshFormats();
    };

    const format = (name, val) => {
      const q = editor();
      if (!q) return;
      q.focus();
      const current = q.getFormat()[name];
      q.format(name, val === undefined ? !current : val, "user");
      refreshFormats();
    };

    const startMention = () => {
      const q = editor();
      if (!q) return;
      const range = q.getSelection(true);
      const prev = range.index ? q.getText(range.index - 1, 1) : "\n";
      q.insertText(range.index, /\s/.test(prev) ? "@" : " @", "user");
      setTimeout(checkMention, 0);
    };

    const openLink = () => {
      setLinkUrl(formats.link || "https://");
      setMenu("link");
    };
    const applyLink = () => {
      const url = linkUrl.trim();
      const q = editor();
      if (!q) return;
      const range = q.getSelection(true);
      if (!url || !/^(https?:\/\/|mailto:)/i.test(url)) {
        format("link", false);
      } else if (range.length === 0 && !formats.link) {
        q.insertText(range.index, url, { link: url }, "user");
        q.setSelection(range.index + url.length, 0);
      } else {
        q.format("link", url, "user");
      }
      setMenu(null);
      refreshFormats();
    };

    const modules = useMemo(() => ({ toolbar: false, clipboard: { matchVisual: false } }), []);
    const showToolbar = !compact || focused || menu;
    const styleLabel = TEXT_STYLES.find((s) => s.value === (formats.header || false))?.label || "Normal text";

    return (
      <div
        ref={wrapRef}
        className={`trello-editor relative rounded-[14px] border bg-panel transition-colors ${
          focused ? "border-accent-line ring-2 ring-accent-soft" : "border-line2"
        } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setFocused(false);
            setTimeout(() => setMention(null), 150);
          }
        }}
      >
        {showToolbar && (
          <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-line">
            <div className="relative">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setMenu(menu === "style" ? null : "style")}
                className="h-8 px-2 rounded-lg flex items-center gap-1 text-[13px] font-medium text-muted hover:text-ink hover:bg-hover"
                aria-label="Text style"
                title="Text style"
              >
                <span className="font-display">Aa</span>
                <span className="hidden sm:inline max-w-[90px] truncate">{styleLabel}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <Menu open={menu === "style"} onClose={() => setMenu(null)} className="left-0 w-44">
                {TEXT_STYLES.map((s) => (
                  <MenuItem
                    key={String(s.value)}
                    active={(formats.header || false) === s.value}
                    onClick={() => {
                      format("header", s.value);
                      setMenu(null);
                    }}
                  >
                    <span className={s.cls}>{s.label}</span>
                  </MenuItem>
                ))}
              </Menu>
            </div>
            <span className="w-px h-5 bg-line mx-1" />
            <ToolButton label="Bold (Ctrl+B)" active={formats.bold} onClick={() => format("bold")}>
              <Bold className="w-4 h-4" />
            </ToolButton>
            <ToolButton label="Italic (Ctrl+I)" active={formats.italic} onClick={() => format("italic")}>
              <Italic className="w-4 h-4" />
            </ToolButton>
            <div className="relative">
              <ToolButton label="More formatting" active={formats.strike || formats.code} onClick={() => setMenu(menu === "more" ? null : "more")}>
                <MoreHorizontal className="w-4 h-4" />
              </ToolButton>
              <Menu open={menu === "more"} onClose={() => setMenu(null)} className="left-0 w-48">
                <MenuItem active={formats.strike} onClick={() => { format("strike"); setMenu(null); }}>
                  <Strikethrough className="w-4 h-4" /> Strikethrough
                </MenuItem>
                <MenuItem active={formats.code} onClick={() => { format("code"); setMenu(null); }}>
                  <Code className="w-4 h-4" /> Inline code
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const q = editor();
                    const r = q?.getSelection(true);
                    if (r) q.removeFormat(r.index, r.length, "user");
                    setMenu(null);
                    refreshFormats();
                  }}
                >
                  <RemoveFormatting className="w-4 h-4" /> Clear formatting
                </MenuItem>
              </Menu>
            </div>
            <span className="w-px h-5 bg-line mx-1" />
            <ToolButton label="Bulleted list" active={formats.list === "bullet"} onClick={() => format("list", formats.list === "bullet" ? false : "bullet")}>
              <List className="w-4 h-4" />
            </ToolButton>
            <ToolButton label="Numbered list" active={formats.list === "ordered"} onClick={() => format("list", formats.list === "ordered" ? false : "ordered")}>
              <ListOrdered className="w-4 h-4" />
            </ToolButton>
            <ToolButton label="Indent (Tab)" disabled={!formats.list || (formats.indent || 0) >= MAX_LIST_INDENT} onClick={() => nest(1)}>
              <Indent className="w-4 h-4" />
            </ToolButton>
            <ToolButton label="Outdent (Shift+Tab)" disabled={!formats.list || !formats.indent} onClick={() => nest(-1)}>
              <Outdent className="w-4 h-4" />
            </ToolButton>
            <span className="w-px h-5 bg-line mx-1" />
            <div className="relative">
              <ToolButton label="Link" active={Boolean(formats.link)} onClick={openLink}>
                <Link2 className="w-4 h-4" />
              </ToolButton>
              <Menu open={menu === "link"} onClose={() => setMenu(null)} className="left-0 w-72 p-2">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    applyLink();
                  }}
                  className="flex gap-1.5"
                >
                  <input
                    autoFocus
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://…"
                    aria-label="Link URL"
                    className="flex-1 min-w-0 h-8 px-2.5 rounded-lg bg-panel2 border border-line2 text-[13px] text-ink focus:outline-none focus:border-accent-line"
                  />
                  <button type="submit" className="h-8 px-3 rounded-lg bg-grad text-on-accent text-[12.5px] font-semibold">
                    {linkUrl.trim() && linkUrl.trim() !== "https://" ? "Apply" : "Remove"}
                  </button>
                </form>
              </Menu>
            </div>
            <ToolButton label="Code block" active={formats["code-block"]} onClick={() => format("code-block")}>
              <SquareCode className="w-4 h-4" />
            </ToolButton>
            <ToolButton label="Quote" active={formats.blockquote} onClick={() => format("blockquote")}>
              <Quote className="w-4 h-4" />
            </ToolButton>
            {mentionable.length > 0 && (
              <ToolButton label="Mention someone" onClick={startMention}>
                <AtSign className="w-4 h-4" />
              </ToolButton>
            )}
          </div>
        )}

        <ReactQuill
          ref={quillRef}
          theme="snow"
          defaultValue={initial}
          readOnly={disabled}
          placeholder={placeholder}
          modules={modules}
          formats={FORMATS}
          onChange={(html, _delta, source, q) => {
            const empty = !q.getText().trim() && !q.getContents().ops.some((op) => op.insert && typeof op.insert === "object");
            onChange?.(empty ? "" : html);
            if (source === "user") setTimeout(checkMention, 0);
          }}
          onChangeSelection={() => {
            refreshFormats();
            checkMention();
          }}
          style={{ "--editor-min-h": `${minHeight}px` }}
          aria-label={ariaLabel}
        />

        {mention && suggestions.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-40 w-64 max-h-56 overflow-y-auto rounded-xl border border-line2 bg-panel-solid shadow-modal p-1"
            style={{ top: mention.top + (showToolbar ? 42 : 0), left: mention.left }}
          >
            {suggestions.map((p, i) => (
              <li key={p._id} role="option" aria-selected={i === highlight}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickMention(p)}
                  className={`flex w-full items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-[13px] ${
                    i === highlight ? "bg-accent-soft text-accent-ink" : "text-ink hover:bg-hover"
                  }`}
                >
                  <Avatar src={p.photo} name={p.fullNameEn} size="xs" />
                  {p.fullNameEn}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);

TrelloEditor.displayName = "TrelloEditor";

export default TrelloEditor;
